import {Mutations, Queries} from '../../api/queries';
import {List} from '../List';
import MobileFriendlyItemDisplay from '../MobileFriendlyItemDisplay';
import QuillEditor, {type QuillContent} from '../QuillEditor';
import Table from '../Table';
import {Box, Button, Icon, Input, LoaderButton, Modal} from '@sproutsocial/racine';
import {useMutation, useQuery} from '@tanstack/react-query';
import {SyntheticEvent, useCallback, useMemo, useState} from 'react';
import {orderBy} from 'shared/src/list';
import type {Note} from 'shared/src/models';

interface ProjectNotesProps {
	projectId: string;
}

const ProjectNotes = ({projectId}: ProjectNotesProps) => {
	const {data: project, refetch: refetchProject} = useQuery(Queries.project(projectId));
	const {data: notes, refetch: refetchNotes} = useQuery({
		...Queries.notes(project?.notes ?? []),
		enabled: Boolean(project),
		select: (notes) => orderBy(notes, 'name'),
	});

	const refetch = useCallback(async () => {
		await Promise.all([refetchProject(), refetchNotes()]);
	}, [refetchProject, refetchNotes]);

	const [editingNote, setEditingNote] = useState<(Omit<Note, 'id'> & {id: string | null}) | null>();
	const onUpdateEditingNoteName = useCallback((event: SyntheticEvent<HTMLInputElement>) => {
		const name = event.currentTarget.value;

		setEditingNote((note) => ({
			...note!,
			name,
		}));
	}, []);
	const onUpdateEditingNoteContent = useCallback((newContent: QuillContent) => {
		setEditingNote((note) => ({
			...note!,
			content: newContent,
		}));
	}, []);

	const {mutateAsync: createNoteAsync, isPending: isCreatingNote} = useMutation(
		Mutations.createNote(),
	);
	const {mutateAsync: updateNoteAsync, isPending: isUpdatingNote} = useMutation(
		Mutations.updateNote(),
	);
	const {mutateAsync: deleteNoteAsync} = useMutation(Mutations.deleteNote());

	const editCallbacks = useMemo(() => {
		return notes?.reduce((callbacks, note) => {
			callbacks.set(note.id, async (event: SyntheticEvent<any>) => {
				event.stopPropagation();

				setEditingNote(note);
			});

			return callbacks;
		}, new Map());
	}, [notes]);

	const deleteCallbacks = useMemo(() => {
		return notes?.reduce((callbacks, note) => {
			callbacks.set(note.id, async (event: SyntheticEvent<any>) => {
				event.stopPropagation();

				await deleteNoteAsync({
					id: note.id,
				});

				await refetch();
			});

			return callbacks;
		}, new Map());
	}, [notes, refetch, deleteNoteAsync]);

	const onCreateNote = useCallback(() => {
		setEditingNote({
			id: null,
			name: '',
			content: {},
		});
	}, []);

	const onCancelEditing = useCallback(() => {
		setEditingNote(null);
	}, []);

	const onSaveNote = useCallback(async () => {
		if (editingNote?.id) {
			await updateNoteAsync({
				note: {
					...editingNote,
					id: editingNote.id!,
				},
			});
		} else {
			await createNoteAsync({
				note: {
					name: editingNote!.name,
					content: editingNote!.content,
				},
				projectId,
			});
		}

		await refetch();

		setEditingNote(null);
	}, [projectId, editingNote, refetch, updateNoteAsync, createNoteAsync]);

	if (!notes || !deleteCallbacks || !editCallbacks) {
		return null;
	}

	return (
		<Box>
			<Box display='flex' justifyContent='flex-end'>
				<Button appearance='primary' onClick={onCreateNote} mb='space.400'>
					<Icon name='plus' mr='space.300' />
					Create Note
				</Button>
			</Box>
			<MobileFriendlyItemDisplay
				list={
					<List id={`project-${projectId}-notes`}>
						{notes.map((note) => (
							<List.Item key={note.id} title={note.name} />
						))}
					</List>
				}
				table={
					<Table
						id={`project-${projectId}-notes`}
						head={[
							{
								id: 'name',
								content: 'Name',
							},
							{
								id: 'actions',
								content: null,
							},
						]}
						items={notes}
						generateRow={(note) => ({
							id: note.id,
							cells: [
								note.name,
								<Box display='flex' justifyContent='flex-end' columnGap='space.300'>
									<Icon
										name='pencil-to-square-solid'
										cursor='pointer'
										onClick={editCallbacks.get(note.id)}
										color='button.primary.background.base'
									/>
									<Icon
										name='trash-solid'
										cursor='pointer'
										onClick={deleteCallbacks.get(note.id)}
										color='button.primary.background.base'
									/>
								</Box>,
							],
							drawerContents: <QuillEditor content={note.content} readOnly />,
						})}
					/>
				}
			/>
			{editingNote && (
				<Modal isOpen label='Edit Note' closeButtonLabel='Close' onClose={onCancelEditing}>
					<Modal.Header title='Edit Note' />
					<Modal.Content>
						<Input
							id='edited-note'
							name='edited-note'
							placeholder='Project Name'
							value={editingNote.name}
							onChange={onUpdateEditingNoteName}
							mb='space.300'
						/>
						<QuillEditor content={editingNote.content} onChange={onUpdateEditingNoteContent} />
					</Modal.Content>
					<Modal.Footer display='flex' justifyContent='flex-end'>
						<Button onClick={onCancelEditing} mr='space.300'>
							Cancel
						</Button>
						<LoaderButton
							appearance='primary'
							onClick={onSaveNote}
							isLoading={isCreatingNote || isUpdatingNote}
							minWidth={80}
							disabled={!editingNote.name.trim() || !editingNote.content}
						>
							{editingNote.id ? 'Save' : 'Create'}
						</LoaderButton>
					</Modal.Footer>
				</Modal>
			)}
		</Box>
	);
};

export default ProjectNotes;
